import { ChangeDetectionStrategy, Component, computed, effect, inject, input, InputSignal, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule, GoogleMap } from '@angular/google-maps';
import { RouteDataService } from '../../services/route-data.service';
import { Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { MapMarker } from '@angular/google-maps';
import { Subscription } from 'rxjs';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { START_POINT_FLAG, DEFAULT_MARKER, SATURATED_MARKER, CLUSTERED_MARKER, RECOMMENDED_MARKER } from './config/constants';
import { Router } from '@angular/router';

@Component({
    selector: 'fitur-map-markers',
    standalone: true,
    imports: [ 
        CommonModule, 
        GoogleMapsModule,
    ],
    templateUrl: './markers.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class FiturMapMarkersComponent {
    @ViewChildren('markerRef', { read: MapMarker }) markers!: QueryList<MapMarker>;
    public map: InputSignal<any> = input<any>();
    
    private markersChangesSub?: Subscription;
    private clusterer?: MarkerClusterer;

    private _router = inject(Router);
    private _routeDataService: RouteDataService = inject(RouteDataService);
    public routeSegments = computed<Point[]>(() => this._routeDataService.getCurrentRouteDay());

    // Start Point ----------------------------------------------------------------------------------------------------------------
    public startPoint = computed<google.maps.LatLngLiteral>(() => this._routeDataService.startPoint());
    public startPointIconOptions: google.maps.MarkerOptions = START_POINT_FLAG();

    // Markers --------------------------------------------------------------------------------------------------------------------
    public defaultMakerOption = (index) => DEFAULT_MARKER(index + 1);
    public saturatedMakerOption = (index) => SATURATED_MARKER(index + 1);
    public recommendedMarkerOption = () => RECOMMENDED_MARKER();

    async ngAfterViewInit() {        
        this.clusterer = new MarkerClusterer({ 
            map: this.map()?.googleMap, 
            markers: [], 
            algorithm: new SuperClusterAlgorithm({ maxZoom: 20, radius: 60 }),
            renderer: this.clusterRenderer(),
        });
        
        this.markersChangesSub = this.markers.changes.subscribe(async () => this.setMarkerClustered());
        this.setMarkerClustered(); 
    }

    public getMarkerInfo(item: Point): void{
        if (!item?.id)
            return;

        const typeNormalized = item.type !== 'event' ? 'point' : 'event';
        this._router.navigate(['/home', typeNormalized, item.id]);
    }

    public getRelatedMarkerInfo(item){
        console.log("Item clicked", item)
    }

    private async setMarkerClustered(){
        this.clusterer?.clearMarkers();

        const gmMarkers = await Promise.all(this.markers.map(m => m._resolveMarker()));
        gmMarkers.forEach((element, index) => {
            const item = this.routeSegments()?.[index];
            element.setPosition(item.currentLocation);
            if (item.relateds){
                element.setOptions(this.saturatedMakerOption(index));
                return;
            }
            element.setOptions(this.defaultMakerOption(index));
            this.clusterer.addMarker(element);
        });

        this.clusterer.render();

        const groups = this.groupNearbyMarkers(gmMarkers, 10);
        groups.forEach(g => {
            if (g.length > 1) 
                this.spiderfyCluster(g, 0.5);
        });
    }

    private groupNearbyMarkers(markers, maxDistanceMeters = 10) {
        const groups = [];
        const used = new Set();

        markers.forEach((m, i) => {
            if (used.has(i)) 
                return;
            
            const cluster = [m];
            for (let j = i + 1; j < markers.length; j++) {
                if (used.has(j)) 
                    continue;

                const d = this.haversineDistance(
                    m.getPosition().toJSON(),
                    markers[j].getPosition().toJSON()
                );

                if (d < maxDistanceMeters) {
                    cluster.push(markers[j]);
                    used.add(j);
                }
            }
            
            used.add(i);
            groups.push(cluster);
        });

        return groups;
    }

    private haversineDistance(p1, p2) {
        const R = 6371000;
        const dLat = (p2.lat - p1.lat) * Math.PI / 180;
        const dLng = (p2.lng - p1.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2 +
                    Math.cos(p1.lat*Math.PI/180) * Math.cos(p2.lat*Math.PI/180) *
                    Math.sin(dLng/2)**2;
        return 2 * R * Math.asin(Math.sqrt(a));
    }

    private spiderfyCluster(markers, radiusMeters = 10) {
        if (!markers?.length) 
            return;

        const center = markers[0].getPosition();
        const count = markers.length;

        const latRad = center.lat() * Math.PI / 180;
        const metersPerDegLat = 111320;
        const metersPerDegLng = 111320 * Math.cos(latRad); 

        const dLat = radiusMeters / metersPerDegLat;
        const dLng = radiusMeters / metersPerDegLng;
        const startAngle = (count === 2) ? Math.PI / 2 : -Math.PI / 2;
        const angleStep = (2 * Math.PI) / count;

        markers.forEach((m, i) => {
            const angle = startAngle + i * angleStep;
            const lat = center.lat() + dLat * Math.cos(angle);
            const lng = center.lng() + dLng * Math.sin(angle);
            m.setPosition({ lat, lng });
        });
    }

    private clusterRenderer() {
        return {
            render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
                return new google.maps.Marker({ position, ...CLUSTERED_MARKER(count)});
            }
        };
    }

    ngOnDestroy() {
        this.clusterer?.setMap(null);
        this.markersChangesSub?.unsubscribe();
        this.markersChangesSub = undefined;
    }
    
}